import React from 'react';
import {
    FormGroup, Button, List,
} from 'reactstrap';
import AsyncSelect from 'react-select/async';
import { debounce } from 'lodash-es';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../../Common';
import { toasty, FormCheckbox } from '../../common/forms/FormElements';
import ValidatedSelect from '../../common/forms/ValidatedSelect';
import { BlockBorder } from '../../common/Block';
import { SaveButton } from '../../common/forms/SaveButton';
import { ModalForm } from '../../common/forms/ModalForm';

/**
 * Shared class component for adding/replacing charges on one or more timesheets.
 * Can be used by both Admin Tools and Support Tools pages.
 *
 * Props:
 * @param {function} onSuccess - Callback after successful submission (e.g., refreshUsageHistory)
 */

class AddChargesToTimesheetVM {
    timesheetIds = [];

    charges = [];
}

class AddChargesToTimesheetForm extends React.Component {
    state = {
        saving: false,
        addChargesToTimesheet: new AddChargesToTimesheetVM(),
        selectedTimesheetOption: [],
        surchargeModalOpen: false,
        applyingCharges: [],
        timesheetCharges: [],
        basedOnChargeTypeOption: null,
        appliedChargeTypeOption: null,
        replaceCharges: false,
    };

    toggleReplaceCharges = ({ target }) =>
        this.setState({ replaceCharges: target.checked });

    toggleSurchargeModal = () =>
        this.setState((prev) => ({
            surchargeModalOpen: !prev.surchargeModalOpen,
            ...(prev.surchargeModalOpen && {
                basedOnChargeTypeOption: null,
                appliedChargeTypeOption: null,
                applyingCharges: [],
            }),
        }));

    onBasedOnChargeTypeChanged = (option) => {
        const { addChargesToTimesheet } = this.state;
        this.setState(
            {
                basedOnChargeTypeOption: option,
                appliedChargeTypeOption: null,
                applyingCharges: [],
            },
            async () => {
                if (option && addChargesToTimesheet.timesheetIds.length > 0) {
                    await this.loadApplyChargeOptions(option.value);
                }
            },
        );
    };

    onAppliedChargeTypeChanged = (option) =>
        this.setState({ appliedChargeTypeOption: option });

    addSurcharge = () => {
        const { basedOnChargeTypeOption, appliedChargeTypeOption, replaceCharges } = this.state;
        if (!basedOnChargeTypeOption || !appliedChargeTypeOption) return;

        const pair = {
            basedOnChargeTypeId: Number(basedOnChargeTypeOption.value),
            basedOnChargeTypeLabel: basedOnChargeTypeOption.label,
            appliedChargeTypeId: Number(appliedChargeTypeOption.value),
            appliedChargeTypeLabel: appliedChargeTypeOption.label,
            replaceCharge: replaceCharges,
        };

        this.setState((prev) => ({
            addChargesToTimesheet: {
                ...prev.addChargesToTimesheet,
                charges: [...prev.addChargesToTimesheet.charges, pair],
            },
            surchargeModalOpen: false,
            basedOnChargeTypeOption: null,
            appliedChargeTypeOption: null,
            applyingCharges: [],
            replaceCharges: false,
        }));
    };

    removeSurcharge = (idx) => {
        this.setState((prev) => ({
            addChargesToTimesheet: {
                ...prev.addChargesToTimesheet,
                charges: prev.addChargesToTimesheet.charges.filter((_, i) => i !== idx),
            },
        }));
    };

    loadTimesheetOptions = debounce((inputValue, callback) => {
        httpClient.js(ApiRoutes.typeAheads.timesheetNumberSearch(inputValue))
            .then((data) => callback(data));
    }, 250);

    loadApplyChargeOptions = async (baseChargeTypeId) => {
        const [timesheetId] = this.state.addChargesToTimesheet.timesheetIds;
        if (!timesheetId) {
            this.setState({ applyingCharges: [] });
            return;
        }
        try {
            const data = await httpClient.js(
                ApiRoutes.timesheet.getSameTypeCharges_timesheet(timesheetId, baseChargeTypeId),
            );
            this.setState({
                applyingCharges: data.map(({ id, description }) => ({
                    value: id,
                    label: description,
                })),
            });
        } catch (err) {
            console.error('Error fetching applying charge types:', err);
            this.setState({ applyingCharges: [] });
        }
    };

    loadTimesheetCharges = async (timesheetId) => {
        try {
            const data = await httpClient.js(
                ApiRoutes.timesheet.getTimesheetCharges(timesheetId),
            );
            this.setState({
                timesheetCharges: data.map(({ id, description }) => ({
                    value: id,
                    label: description,
                })),
            });
        } catch (err) {
            console.error('Error fetching timesheet charge types:', err);
            this.setState({ timesheetCharges: [] });
        }
    };

    onTimesheetChanged = (selectedOptions) => {
        const timesheetIds = selectedOptions?.map((o) => Number(o.value)) ?? [];
        this.setState(
            (prev) => ({
                addChargesToTimesheet: { ...prev.addChargesToTimesheet, timesheetIds },
                selectedTimesheetOption: selectedOptions,
            }),
            () => {
                if (timesheetIds.length > 0) {
                    void this.loadTimesheetCharges(timesheetIds[0]);
                } else {
                    this.setState({ timesheetCharges: [] });
                }
            },
        );
    };

    onSubmit = async (e) => {
        if (e) e.preventDefault();

        const { addChargesToTimesheet } = this.state;

        if (!addChargesToTimesheet.timesheetIds.length) {
            toasty.error('Please select at least one timesheet.');
            return;
        }

        if (!addChargesToTimesheet.charges.length) {
            toasty.error('Please add at least one charge.');
            return;
        }

        this.setState({ saving: true });

        try {
            await httpClient.post(
                ApiRoutes.adminTool.addChargesToTimesheet(),
                addChargesToTimesheet,
            );

            this.setState({
                addChargesToTimesheet: new AddChargesToTimesheetVM(),
                selectedTimesheetOption: [],
                timesheetCharges: [],
                applyingCharges: [],
                basedOnChargeTypeOption: null,
                appliedChargeTypeOption: null,
                replaceCharges: false,
            });

            toasty.success('Add Charge(s) Succeeded');

            if (this.props.onSuccess) {
                await this.props.onSuccess();
            }
        } catch (err) {
            console.error('Error adding charges to timesheet:', err);
            toasty.error('Error: Add Charges to Timesheet Failed. Please check Timesheet and Charges');
            if (this.props.onSuccess) {
                await this.props.onSuccess();
            }
        } finally {
            this.setState({ saving: false });
        }
    };

    renderSurchargeModal = () => {
        const {
            surchargeModalOpen, timesheetCharges, applyingCharges,
            basedOnChargeTypeOption, appliedChargeTypeOption,
        } = this.state;
        const isAddChargeDisabled = !basedOnChargeTypeOption || !appliedChargeTypeOption;

        return (
            <ModalForm
                isOpen={surchargeModalOpen}
                onSubmit={() => this.addSurcharge()}
                toggle={() => this.toggleSurchargeModal()}
                title="Add or Replace a Charge"
                SaveButtonProps={{ label: 'Add', disabled: isAddChargeDisabled }}
            >
                <ValidatedSelect
                    id="basedOnChargeType"
                    name="basedOnChargeType"
                    label="Charges are based on"
                    options={timesheetCharges}
                    value={basedOnChargeTypeOption}
                    onChange={this.onBasedOnChargeTypeChanged}
                    placeholder="Select a charge type"
                    error="You must select a charge type"
                    required
                />

                <ValidatedSelect
                    id="appliedChargeType"
                    label="Charge Type to add"
                    name="appliedChargeType"
                    options={applyingCharges}
                    value={appliedChargeTypeOption}
                    onChange={this.onAppliedChargeTypeChanged}
                    placeholder="Select a charge type to add"
                    error="You must select a charge type"
                    required
                    isDisabled={
                        !this.state.addChargesToTimesheet.timesheetIds.length
                        || !basedOnChargeTypeOption
                        || !applyingCharges.length
                    }
                />

                <FormGroup>
                    <FormCheckbox
                        id="replaceCharges"
                        name="replaceCharges"
                        checked={this.state.replaceCharges}
                        onChange={this.toggleReplaceCharges}
                        labelText="Replace Charges"
                    />
                </FormGroup>
            </ModalForm>
        );
    };

    render() {
        const { saving } = this.state;
        const isSubmitDisabled = saving
            || !this.state.addChargesToTimesheet.timesheetIds.length
            || !this.state.addChargesToTimesheet.charges.length;

        return (
            <>
                <form onSubmit={this.onSubmit}>
                    <ValidatedSelect
                        component={AsyncSelect}
                        label="Timesheet Number(s)"
                        loadOptions={this.loadTimesheetOptions}
                        placeholder="Type Timesheet Numbers Here"
                        id="selectedTimesheetOption"
                        name="selectedTimesheetOption"
                        value={this.state.selectedTimesheetOption}
                        onChange={this.onTimesheetChanged}
                        isMulti
                        required
                        error="At least one Timesheet Number is required"
                    />

                    <div className="d-flex mb-3">
                        <Button
                            color="secondary"
                            onClick={this.toggleSurchargeModal}
                        >
                            Add/Replace Charges
                        </Button>
                    </div>

                    {this.state.addChargesToTimesheet.charges.length > 0 && (
                        <>
                            <h6 className="font-weight-bold">
                                Charges Scheduled for Addition or Replacement
                            </h6>
                            <List className="list-group mb-3">
                                {this.state.addChargesToTimesheet.charges.map((s, idx) => (
                                    <li
                                        key={idx}
                                        className="list-group-item d-flex justify-content-between align-items-center"
                                    >
                                        <span>
                                            <strong>{s.appliedChargeTypeLabel}</strong>
                                            {' -> '}
                                            <strong>{s.basedOnChargeTypeLabel}</strong>
                                            {' '}
                                            (
                                            {s.replaceCharge ? 'Replace' : 'Add'}
                                            )
                                        </span>
                                        <Button
                                            close
                                            aria-label="Remove"
                                            onClick={() => this.removeSurcharge(idx)}
                                        />
                                    </li>
                                ))}
                            </List>
                        </>
                    )}

                    <BlockBorder />

                    <div className="d-flex justify-content-center">
                        <SaveButton
                            saving={saving}
                            disabled={isSubmitDisabled}
                            label="Submit"
                        />
                    </div>
                </form>

                {this.renderSurchargeModal()}
            </>
        );
    }
}

/**
 * Renders the execution history details for AddChargesToTimesheet.
 * Can be used by both Admin and SupportTools ExecutionHistory components.
 */
export const AddChargesToTimesheetHistoryDetails = ({ record }) => (
    <ul className="list-unstyled">
        {record.input?.timesheet_numbers?.length > 0 && (
            <li>
                <strong>Timesheets:&nbsp;</strong>
                {record.input.timesheet_numbers.join(', ')}
            </li>
        )}

        {record.input?.charges?.length > 0 && (
            <li>
                <strong>Requested Changes:</strong>
                <ul className="mb-0">
                    {record.input.charges.map((c, i) => (
                        <li key={i}>
                            {c.text}
                            {' '}
                            {c.replaced ? '(Replace)' : '(Add)'}
                        </li>
                    ))}
                </ul>
            </li>
        )}

        {record.output?.added_charges?.length > 0 && (
            <li>
                <strong>Charges Changed:</strong>
                <ul className="mb-0">
                    {record.output.added_charges.map((s, i) => (
                        <li key={i}>
                            {s.applied}
                            {' '}
                            ({s.action})
                        </li>
                    ))}
                </ul>
            </li>
        )}

        {record.output?.failed_timesheets?.length > 0 && (
            <li>
                <strong>Failed to Make Changes on Timesheet(s):&nbsp;</strong>
                {record.output.failed_timesheets.join(', ')}
            </li>
        )}
    </ul>
);

export { AddChargesToTimesheetForm };
export default AddChargesToTimesheetForm;
